import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  CalendarIcon,
  TrophyIcon,
  InformationCircleIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  NewspaperIcon,
  Squares2X2Icon,
  UsersIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  BanknotesIcon,
  CogIcon,
  DocumentTextIcon,
  MapPinIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowRightOnRectangleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  CalendarIcon as CalendarIconSolid,
  TrophyIcon as TrophyIconSolid,
  Bars3Icon as Bars3IconSolid,
} from '@heroicons/react/24/solid';
import { supabase } from '../lib/supabase';
import LoginModal from '../components/LoginModal';
import { toast } from 'react-hot-toast';
import type { Logo } from '../types/content';
import { useCurrentUser } from '../hooks/useCurrentUser';
import type { AdminTab } from '../lib/rolePermissions';

interface NavItem {
  label: string;
  path: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  ActiveIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tab?: AdminTab;
}

const PUBLIC_NAV: NavItem[] = [
  { label: 'Home', path: '/t', Icon: HomeIcon, ActiveIcon: HomeIconSolid },
  { label: 'Schedule', path: '/t/schedule', Icon: CalendarIcon, ActiveIcon: CalendarIconSolid },
  { label: 'Leaderboard', path: '/t/leaderboard', Icon: TrophyIcon, ActiveIcon: TrophyIconSolid },
  { label: 'History', path: '/t/history', Icon: ClockIcon },
  { label: 'Registration', path: '/t/registration', Icon: ClipboardDocumentListIcon },
  { label: 'Partners', path: '/t/sponsors', Icon: UserGroupIcon },
  { label: 'About', path: '/t/about', Icon: InformationCircleIcon },
  { label: 'Prize Breakdown', path: '/t/prize-breakdown', Icon: TrophyIcon },
  { label: 'Press', path: '/t/press', Icon: NewspaperIcon },
];

const ADMIN_NAV: NavItem[] = [
  { label: 'Dashboard',   path: '/t/admin',             Icon: Squares2X2Icon,          tab: 'dashboard' },
  { label: 'Tournaments', path: '/t/admin/tournaments', Icon: TrophyIcon,              tab: 'tournaments' },
  { label: 'Players',     path: '/t/admin/players',     Icon: UsersIcon,               tab: 'players' },
  { label: 'Partners',    path: '/t/admin/partners',    Icon: BuildingOfficeIcon,      tab: 'partners' },
  { label: 'Events',      path: '/t/admin/events',      Icon: CalendarIcon,            tab: 'calendar' },
  { label: 'Finance',     path: '/t/admin/finance',     Icon: BanknotesIcon,           tab: 'finance' },
  { label: 'Analytics',   path: '/t/admin/analytics',   Icon: ChartBarIcon,            tab: 'analytics' },
  { label: 'Press',       path: '/t/admin/press',       Icon: NewspaperIcon,           tab: 'press' },
  { label: 'Content',     path: '/t/admin/content',     Icon: DocumentTextIcon,        tab: 'contents' },
  { label: 'Courses',     path: '/t/admin/courses',     Icon: MapPinIcon,              tab: 'courses' },
  { label: 'Scores',      path: '/t/admin/scores',      Icon: ClipboardDocumentListIcon, tab: 'scores' },
  { label: 'Messaging',   path: '/t/admin/messaging',   Icon: ChatBubbleLeftRightIcon, tab: 'messaging' },
  { label: 'Settings',    path: '/t/admin/settings',    Icon: CogIcon,                 tab: 'settings' },
];

export default function TabletSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<any>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [logo, setLogo] = useState<Logo | null>(null);
  const { user: currentUser } = useCurrentUser();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));

    supabase
      .from('logos')
      .select('*')
      .eq('type', 'primary')
      .eq('location', 'header')
      .eq('is_active', true)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setLogo(data));

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/t/admin')) {
      setAdminExpanded(true);
    }
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/t') return location.pathname === '/t' || location.pathname === '/t/';
    return location.pathname.startsWith(path);
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
      toast.success('Signed out');
      navigate('/t');
    } catch {
      toast.error('Failed to sign out');
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <aside className="fixed top-0 left-0 h-full w-56 bg-white border-r border-gray-100 flex flex-col z-40 overflow-y-auto">
      <div className="px-4 py-5 border-b border-gray-100 flex-shrink-0">
        {logo ? (
          <img src={logo.url} alt="Ibom Open" className="h-9 w-auto" />
        ) : (
          <span className="text-golf-orange font-display font-bold text-xl leading-tight">Ibom Open</span>
        )}
        <p className="text-[10px] text-gray-400 mt-0.5 uppercase tracking-wide">Golf Tournament</p>
      </div>

      <nav className="flex-1 px-2 py-3 space-y-0.5">
        {PUBLIC_NAV.map(item => {
          const active = isActive(item.path);
          const Icon = (active && item.ActiveIcon) ? item.ActiveIcon : item.Icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                active
                  ? 'bg-golf-orange/10 text-golf-orange'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4.5 h-4.5 flex-shrink-0 w-5 h-5" />
              {item.label}
            </Link>
          );
        })}

        <div className="pt-3">
          <button
            onClick={() => setAdminExpanded(v => !v)}
            className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors"
          >
            <span>Admin</span>
            {adminExpanded ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
          </button>

          {adminExpanded && (
            <div className="space-y-0.5 mt-0.5">
              {session ? (
                ADMIN_NAV.filter(item =>
                  !item.tab || !currentUser || currentUser.allowedTabs.includes(item.tab)
                ).map(item => {
                  const active = isActive(item.path) && (item.path !== '/t/admin' || location.pathname === '/t/admin' || location.pathname === '/t/admin/');
                  const Icon = item.Icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        active
                          ? 'bg-golf-orange/10 text-golf-orange'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {item.label}
                    </Link>
                  );
                })
              ) : (
                <button
                  onClick={() => setLoginOpen(true)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                  Admin Login
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      {session && (
        <div className="px-2 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 flex-shrink-0" />
            {signingOut ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>
      )}

      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} mobileRedirect="/t/admin" />
    </aside>
  );
}
